/**
 * Web Conversation Page
 *
 * Clean composition layer using the Facade pattern.
 *
 * Architecture:
 * - Uses shared useConversationPageFacade from @ideoz/core (100% reusable!)
 * - Injects web-specific implementations (navigation, auth)
 * - Passes organized facade to ConversationPageTemplate
 *
 * Benefits:
 * - Single source of truth for conversation logic
 * - Easy to test (mock one facade)
 * - Platform-agnostic template
 * - Consistent pattern with LandingPage
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '@nanostores/react';
import { hotEditStore, workbenchStore } from '@ideoz/workbench';
import { useParams, useSearchParams } from 'react-router-dom';
import { showToast } from '@ideoz/ui';
import { ConversationModule } from '../modules/conversation';
import {
  ConversationProvider,
  type ConversationWorkbench,
} from '../modules/conversation/context/ConversationContext';
import {
  useConversationPageFacade,
  useAppDispatch,
  useAppSelector,
  getUserProfile,
  getAccessToken,
  WORKBENCH_BUILD_STREAM_TRIGGER,
  patchMessageByLlmInteractionId,
  type Message,
} from '@ideoz/core';
import { useWebNavigation } from '../hooks/useWebNavigation';
import { useAuthDialog } from '../hooks/useAuthDialog';
import { useWorkbenchPrewarm } from '../hooks/useWorkbenchPrewarm';
import { LIGHT_PREVIEW_REQUESTED } from '../modules/prototyper/useLightPreviewRuntime';
import {
  usePrototyperWorkbenchFacade,
  WorkbenchPanel,
} from '../modules/prototyper';
import { addBuiltTurnId } from '../modules/prototyper/utils/builtTurnIds';
import { getStreamingPublishDisabledReason } from '../modules/prototyper/utils/publishDisabledReason';
import { demoLinkIdForTurn } from '../modules/prototyper/utils/demoLinkForTurn';
import { useDesignSystemFromEditor } from '../modules/prototyper/useDesignSystemFromEditor';

/**
 * Ceiling on how long `workbenchPreparing` may keep the full-panel prototyper animation up.
 *
 * That overlay renders *instead of* `PrototyperPanel`'s children, so while it is showing,
 * `WorkbenchPanel` is unmounted — and with it every recovery affordance the workbench has
 * (boot-error card, Retry, Refresh page, Restart preview). Any step underneath that fails to
 * settle therefore produced a permanent, textless, uncancellable animation.
 *
 * The timer restarts whenever boot/bootstrap state actually changes, so a slow-but-progressing
 * restore is never cut off; only a genuinely stalled one expires. On expiry the children mount
 * and `WorkbenchPanel`'s own gate ladder takes over with real text and working actions. Kept
 * well under `WEBCONTAINER_BOOT_TIMEOUT_MS` (45s) so the user reaches a usable state first.
 */
const WORKBENCH_PREPARING_MAX_MS = 20_000;

export default function ConversationPage({
  sidebarPageType,
  urlConversationIdOverride,
  scopeModeControl,
}: {
  sidebarPageType?: 'scope';
  urlConversationIdOverride?: string | null;
  scopeModeControl?: {
    activeMode: 'clarify' | 'prototyper' | 'strategy';
    onModeChange: (mode: 'clarify' | 'prototyper' | 'strategy') => void;
    conversations?: Array<{ id: string; title: string }>;
    isLoadingConversations?: boolean;
    activeConversationId?: string | null;
    onSelectConversation?: (conversationId: string) => void;
    onCreateConversation?: () => void;
    onDeleteConversation?: (conversationId: string) => void;
    isCreatingConversation?: boolean;
    /** True on the explicit `…/new` route — triggers the one auto-prompt greeting. */
    isNewChat?: boolean;
    onBack?: () => void;
    onConversationNotFound?: () => void;
    scopeTitle?: string;
    headerMenuItems?: any[];
    onOpenFiles?: () => void;
    onOpenArtifacts?: () => void;
  };
} = {}): JSX.Element {
  // ========== Get URL Parameters ==========
  const { id: urlConversationId, startupSlug } = useParams<{ id?: string; startupSlug?: string }>();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  // Get mode from URL parameters (for plugin redirects)
  const urlMode = searchParams.get('mode') as 'prototyper' | 'clarify' | null;
  const restoreInteraction = searchParams.get('restoreInteraction');
  const fromLibrary = searchParams.get('from') === 'library';
  const openArtifactIdFromLibrary = searchParams.get('artifact');
  const openArtifactInteractionId = searchParams.get('interaction');

  // ========== Platform-Specific Implementations ==========
  const navigation = useWebNavigation();
  const authDialog = useAuthDialog();

  // Callback to refresh user profile (to update credit balance after prototype creation)
  const handleRefreshUserProfile = useCallback(async () => {
    const accessToken = getAccessToken();
    if (accessToken) {
      await dispatch(getUserProfile(accessToken)).unwrap();
    }
  }, [dispatch]);

  // Adapt authDialog to AuthFacade interface
  const auth = React.useMemo(
    () => ({
      isOpen: authDialog.isOpen,
      mode: authDialog.mode,
      isLoading: authDialog.isLoading,
      openLogin: authDialog.openLogin,
      openRegister: authDialog.openRegister,
      close: authDialog.closeDialog,
    }),
    [authDialog]
  );

  // ========== Shared Facade (100% Reusable) ==========
  const facade = useConversationPageFacade({
    navigation,
    auth,
    showToast,
    urlConversationId: urlConversationId || null,
    initialMode: urlMode || undefined, // Pass URL mode to facade for initialization
    onRefreshUserProfile: handleRefreshUserProfile,
    sidebarPageType,
    urlConversationIdOverride,
    scopeModeControl,
  });

  // WebContainer streaming: use POST …/build-stream (via useChat) whenever the page is cross-origin isolated.
  // Do not gate on "prototyper" mode — that flag may not have flushed before the x-prototyper header effect runs.
  const isStreamingPrototype = typeof window !== 'undefined' && window.crossOriginIsolated === true;
  // Prewarm only where a prototype can be opened (WC-2); the app shell no longer does it.
  // Not with the light preview: prewarming boots a WebContainer and mounts the template during
  // idle time — hundreds of MB, a StackBlitz iframe and a Vite dev server — for a runtime the
  // tab is not going to use. It was why the workbench's log still said "npx vite" with the
  // light preview on screen. A fallback to the WebContainer boots on demand instead.
  useWorkbenchPrewarm(isStreamingPrototype && !LIGHT_PREVIEW_REQUESTED);

  const prototyperDemoLinkId = useAppSelector(
    (s) => s.prototyper?.currentPrototyper?.prototyper_demo_link_id ?? null
  );

  // Only boot the WebContainer / prototyper workbench when the user is
  // actually in prototyper mode. Without this gate, Clarify users incur the
  // full StackBlitz boot cost (dozens of CDN fetches +
  // /prototype/snapshot/finalize) every time they open a conversation, which
  // showed up as huge network noise. Switching mode → prototyper later flips
  // this back to true and boots the workbench on demand.
  const needsImmediateWorkbenchBoot = Boolean(
    facade.state.currentProjectId &&
    facade.state.currentConversationId &&
    facade.mode.currentMode === 'prototyper'
  );

  /** Prototype panel visibility (streaming WebContainer) — defer tmp-chat boot until this is true or a generation runs. */
  const [streamingPrototypePanelVisible, setStreamingPrototypePanelVisible] = useState(false);

  /** Drives per–assistant-turn build timeline + Nest `prototyper_build_sessions`. */
  const [streamingWorkbenchLlmInteractionId, setStreamingWorkbenchLlmInteractionId] = useState<
    string | null
  >(null);

  /** Per build-stream Nest→Flask `llmInteractionId` — set synchronously before `startGeneration`. */
  const streamingBuildLlmInteractionIdRef = useRef<string | undefined>(undefined);
  /** Defer `startGeneration` until `streamingWorkbenchLlmInteractionId` commits (stable `useChat` id). */
  const pendingWorkbenchStreamStartRef = useRef(false);
  /** Incremented on each start request so retry re-fires even when llmInteractionId is unchanged. */
  const [workbenchStreamStartNonce, setWorkbenchStreamStartNonce] = useState(0);

  /**
   * The label stored on the turn's build session (`prototyper_build_sessions.
   * prototype_display_name`): the last thing the founder typed, which is what the build
   * timeline is about. It is deliberately *not* the prototype's name -- the pill takes that
   * from `prototypeTitle` via `resolvePrototypeTitle`, because a turn opened with "yes" made a
   * prototype called "yes". Rows written before that change still read their label, which is
   * the resolver's last rung.
   */
  const getPrototypeDisplayName = useCallback((): string | undefined => {
    const msgs: Message[] = facade.state.messages;
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      if (msgs[i].role === 'user') {
        const t = msgs[i].content?.trim();
        if (t) {
          return t.length > 160 ? `${t.slice(0, 157)}…` : t;
        }
      }
    }
    return undefined;
  }, [facade.state.messages]);

  const onPatchMessageByLlmInteractionId = useCallback(
    (llmInteractionId: string, patch: Record<string, unknown>) => {
      dispatch(
        patchMessageByLlmInteractionId({ llmInteractionId, patch: patch as Partial<Message> })
      );
    },
    [dispatch]
  );

  const handleBuildStreamTurnCompleted = useCallback(
    (llmInteractionId: string) => {
      addBuiltTurnId(facade.state.currentConversationId ?? undefined, llmInteractionId);
    },
    [facade.state.currentConversationId]
  );

  const handleSnapshotRestored = useCallback(
    (meta: { llmInteractionId?: string | null; buildVerified?: boolean }) => {
      if (meta.buildVerified && meta.llmInteractionId) {
        addBuiltTurnId(facade.state.currentConversationId ?? undefined, meta.llmInteractionId);
      }
    },
    [facade.state.currentConversationId]
  );

  /**
   * Automatic fixes (P6.4b) start like any build-stream. The facade hook runs before
   * `handleStreamingPrototypeStart` exists, so it reaches it through this ref.
   */
  const startAutoFixStreamRef = useRef<(llmInteractionId: string) => void>(() => {});
  const handleAutoFixReady = useCallback(
    (llmInteractionId: string) => startAutoFixStreamRef.current(llmInteractionId),
    []
  );
  const planDetailsRef = useRef(facade.state.planDetails);
  planDetailsRef.current = facade.state.planDetails;
  /** Same rule as ConversationModule: a plan without a prototype credit entry does not block. */
  const hasPrototypeCreditsForFix = useCallback((): boolean => {
    const credits = (
      planDetailsRef.current as { credits?: Array<{ type?: string; balance?: unknown }> } | null
    )?.credits;
    const prototypeCredit = credits?.find((c) => c.type === 'prototype');
    return prototypeCredit == null || Number(prototypeCredit.balance) > 0;
  }, []);

  const {
    startGeneration,
    stop: stopWorkbenchStream,
    stopAndAbort: stopAndAbortWorkbenchStream,
    buildTimeline: workbenchBuildTimeline,
    isLoading: workbenchStreamLoading,
    isStreaming: workbenchIsStreaming,
    buildStatus: workbenchBuildStatus,
    bootStatus: workbenchBootStatus,
    previewUrl: workbenchPreviewUrl,
    snapshotBootstrap: workbenchSnapshotBootstrap,
    snapshotRestoreError: workbenchSnapshotRestoreError,
    snapshotRestoreNotice: workbenchSnapshotRestoreNotice,
    snapshotRestoreRetrying: workbenchSnapshotRestoreRetrying,
    workbenchPersistError,
    restorePrototypeSnapshotForInteraction,
    uploadVerifiedPrototypeSnapshot,
    resumeInterruptedBuild,
    activeSnapshotInteractionId: workbenchActiveSnapshotInteractionId,
    restoreInteractionLoadingId: workbenchRestoreInteractionLoadingId,
    snapshotIndexLoading: workbenchSnapshotIndexLoading,
    getSnapshotIndexEntry: workbenchGetSnapshotIndexEntry,
    retrySnapshotBootstrap: workbenchRetrySnapshotBootstrap,
    versionAttention: workbenchVersionAttention,
    versionAttentionCanUndo: workbenchVersionAttentionCanUndo,
    versionAttentionBusy: workbenchVersionAttentionBusy,
    autoFixLabel: workbenchAutoFixLabel,
    tryAnotherFix: workbenchTryAnotherFix,
    undoFailedChange: workbenchUndoFailedChange,
    loadVersionAttentionDetails: workbenchLoadVersionAttentionDetails,
    turnVersions: workbenchTurnVersions,
    resumeFailedVersion: workbenchResumeFailedVersion,
    resumeDraftFromServerFiles: workbenchResumeDraftFromServerFiles,
    repairPreviewCrash: workbenchRepairPreviewCrash,
  } = usePrototyperWorkbenchFacade({
    conversationId: facade.state.currentConversationId ?? undefined,
    projectId: facade.state.currentProjectId ?? undefined,
    needsImmediateWorkbenchBoot,
    streamingWorkbenchPrototypePanelVisible: streamingPrototypePanelVisible,
    modelProvider: facade.prototyper.prototypeGenerationModel.modelProvider,
    modelChoice: facade.prototyper.prototypeGenerationModel.modelChoice,
    onCreditsRefresh: handleRefreshUserProfile,
    // projectCreated SSE carries Chef's project id — never call setCurrentProjectId with it
    // (Nest URLs need the Ideoz UUID from conversation details / route).
    onStreamDemoLinkId: facade.prototyper.setStreamDemoLinkId,
    streamingBuildLlmInteractionIdRef,
    streamingLlmInteractionId: streamingWorkbenchLlmInteractionId ?? undefined,
    getPrototypeDisplayName,
    onPatchMessageByLlmInteractionId,
    onBuildStreamTurnCompleted: handleBuildStreamTurnCompleted,
    onSnapshotRestored: handleSnapshotRestored,
    hasPrototypeCredits: hasPrototypeCreditsForFix,
    onAutoFixReady: handleAutoFixReady,
    preferredRestoreInteractionId: restoreInteraction,
  });

  const stopWorkbenchStreamRef = useRef(stopWorkbenchStream);
  const lastResetConversationIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    stopWorkbenchStreamRef.current = stopWorkbenchStream;
  }, [stopWorkbenchStream]);

  // A colour the founder set "Everywhere" belongs to the startup's design system, which is what
  // every generation is built from - not just to this prototype's stylesheet.
  useDesignSystemFromEditor(facade.state.currentProjectId);
  /**
   * True only once the preview iframe is genuinely showing the generated app (not just once
   * `buildStatus` says success) — see the comment on the store atom itself for why that
   * distinction exists. Gates "View Insights" and Publish so they don't appear over what's
   * still a loading screen.
   */
  const workbenchPreviewReady = useStore(workbenchStore.previewReady);
  const workbenchHasUnbuiltEdits = useStore(workbenchStore.hasUnbuiltEdits);

  /**
   * The publish record is one slot in the store, so it outlives the prototype it describes.
   * Switching conversation drops it: the effect below loads the new prototype's record if it
   * has one, and a conversation with no prototype at all never reaches that effect, because
   * there is no restored snapshot to key it on.
   */
  /**
   * Which demo-link id this conversation has already asked the server about. The fetch changes
   * the store, which re-runs the effect below, so without this a 404 (a deleted demo link, an
   * id from another environment) turned into an endless `GET /prototyper/<id>` loop.
   */
  /**
   * Demo-link ids this conversation asked about and did not get (a deleted link, an id from
   * another environment). The request itself changes the store, which re-runs the effect below,
   * so without remembering the failures a 404 became an endless `GET /prototyper/<id>` loop.
   * Only failures: remembering every id asked would also block the reload that a prototype
   * switch needs.
   */
  const failedDemoLinkIdsRef = useRef<Set<number>>(new Set());

  /**
   * A publish finished. The store already has the new slug and link; this puts the link on the
   * turn as well, which is what the effect below reads — so switching prototype and coming back
   * still knows this one is published, without waiting for a page reload to refetch history.
   */
  const handlePrototypePublished = useCallback(
    ({ demoLinkId }: { demoLinkId: number | null; slug: string | null }) => {
      const lid =
        workbenchActiveSnapshotInteractionId ?? streamingBuildLlmInteractionIdRef.current ?? null;
      if (!lid || demoLinkId === null) return;
      // A link that just published exists, whatever an earlier lookup of that id concluded.
      failedDemoLinkIdsRef.current.delete(demoLinkId);
      // Only the link id: the slug lives on the record the effect below loads by that id, and
      // inventing a message field for it would give the same fact two homes.
      onPatchMessageByLlmInteractionId(lid, { prototyper_demo_link_id: demoLinkId });
    },
    [workbenchActiveSnapshotInteractionId, onPatchMessageByLlmInteractionId],
  );

  useEffect(() => {
    failedDemoLinkIdsRef.current = new Set();
    facade.prototyper.clearPrototyperRecord();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facade.state.currentConversationId]);

  /**
   * Backfill the Redux demo-link record when a past prototype is restored (bootstrap
   * auto-restore on landing, or an explicit "View prototype" pill click) — neither
   * restore path dispatches anything the way a live `demoLinkCreated` SSE event does,
   * so `prototyperDemoLinkId` (and therefore `streamingPublishReady` below) stayed null
   * after a restore even though the build itself succeeded. The id already exists on
   * the restored turn's persisted message — just needs syncing.
   *
   * Fetches the full record (not just the id) — a `setStreamDemoLinkId`-only backfill
   * left `currentPrototyper.slug` null after a restore, so `PrototyperPublishMenu` (which
   * gates "already published" on that slug) showed the prototype as unpublished and
   * prompted to publish again even when it had a live slug on the server.
   *
   * No-ops for the live path: `demoLinkCreated` already patched the same value onto
   * both Redux and the message by the time a live turn's `activeSnapshotInteractionId`
   * updates, so `numId !== prototyperDemoLinkId` is false there.
   *
   * Prefers the `/prototype/snapshots` index entry's `prototyperDemoLinkId` (resolved
   * server-side from `prototyper_demo_links` by `llm_interaction_id`) over the message's
   * own `prototyper_demo_link_id` field — the AI API's llm-interaction listing doesn't
   * reliably carry that field back on a resumed/restored turn, which left this backfill
   * permanently disabled (see prior `//void fetchPrototyperById` here) and the Publish
   * menu stuck on "Publish" for prototypes that were already live.
   */
  useEffect(() => {
    if (!workbenchActiveSnapshotInteractionId) return;
    // The turn's own index entry or message, else another version of the same prototype: a
    // version built after its prototype was published has no id of its own yet
    // (`demoLinkIdForTurn`). Empty fields read as no id (`Number('')` is 0).
    const numId = demoLinkIdForTurn({
      turnId: workbenchActiveSnapshotInteractionId,
      turns: facade.state.messages
        .filter((m) => m.role === 'assistant' && m.llmInteractionId)
        .map((m) => ({
          llmInteractionId: m.llmInteractionId as string,
          demoLinkId: m.prototyper_demo_link_id,
        })),
      indexDemoLinkId: (lid) => workbenchGetSnapshotIndexEntry?.(lid)?.prototyperDemoLinkId,
      prototypeOf: (lid) =>
        workbenchTurnVersions?.status === 'ready'
          ? (workbenchTurnVersions.get(lid)?.prototypeId ?? null)
          : null,
    });
    if (numId === null) {
      // This prototype has no demo link, so it has never been published. Leaving the record
      // that is in the store would show it as published — under the previous prototype's link,
      // which is what "Publish" reported for prototypes nobody had published.
      if (prototyperDemoLinkId !== null) facade.prototyper.clearPrototyperRecord();
      return;
    }
    if (numId !== prototyperDemoLinkId && !failedDemoLinkIdsRef.current.has(numId)) {
      // The whole record, not just the id: "already published" is decided by the slug, and an
      // id-only backfill left it null — so a prototype that was published showed as unpublished
      // again after a refresh, and publishing it a second time made a second link.
      void facade.prototyper.fetchPrototyperById(numId).catch(() => {
        // The demo-link row may be gone (deleted prototype, a link from another environment).
        // Nothing to show as published then, which is what the cleared record says; the id is
        // remembered as failed so this is asked once rather than on every re-render.
        failedDemoLinkIdsRef.current.add(numId);
        facade.prototyper.clearPrototyperRecord();
      });
    }
  }, [
    workbenchActiveSnapshotInteractionId,
    workbenchGetSnapshotIndexEntry,
    workbenchTurnVersions,
    facade.state.messages,
    prototyperDemoLinkId,
    facade.prototyper,
  ]);

  // Note: `prototyperDemoLinkId` is intentionally NOT required here — it's only used
  // (with a fallback below) to build a nicer-looking suggested slug in the Publish
  // menu, not by the publish call itself. It only arrives via the live `demoLinkCreated`
  // SSE event, so a resumed/restored build (which never replays that event) would
  // otherwise be stuck with Publish permanently hidden despite having a working build.
  const streamingPublishReady =
    workbenchBuildStatus === 'success' &&
    Boolean(workbenchPreviewUrl) &&
    !workbenchStreamLoading &&
    !workbenchIsStreaming &&
    !facade.state.isStreaming &&
    workbenchPreviewReady &&
    !workbenchHasUnbuiltEdits;

  const streamingPublishDisabledReason = getStreamingPublishDisabledReason({
    isStreaming: workbenchIsStreaming,
    streamLoading: workbenchStreamLoading,
    facadeIsStreaming: facade.state.isStreaming,
    hasUnbuiltEdits: workbenchHasUnbuiltEdits,
    buildStatus: workbenchBuildStatus,
    hasPreviewUrl: Boolean(workbenchPreviewUrl),
    previewReady: workbenchPreviewReady,
  });

  const workbenchPreparingActive = useMemo(
    () =>
      isStreamingPrototype &&
      (workbenchBootStatus === 'booting' ||
        // A "Retry restore" keeps the workbench mounted (with a "Restoring…" banner) instead
        // of swapping it for the preparing overlay, which read as a blank white panel.
        (workbenchBootStatus === 'ready' &&
          workbenchSnapshotBootstrap === 'pending' &&
          !workbenchSnapshotRestoreRetrying)),
    [
      isStreamingPrototype,
      workbenchBootStatus,
      workbenchSnapshotBootstrap,
      workbenchSnapshotRestoreRetrying,
    ]
  );

  /** See {@link WORKBENCH_PREPARING_MAX_MS} — bounds the otherwise-permanent overlay. */
  const [workbenchPreparingStalled, setWorkbenchPreparingStalled] = useState(false);

  useEffect(() => {
    if (!workbenchPreparingActive) {
      setWorkbenchPreparingStalled(false);
      return undefined;
    }
    // Re-runs (and so restarts the clock) whenever boot/bootstrap state advances, because both
    // are in the dep list — progress keeps the overlay, a stall retires it.
    setWorkbenchPreparingStalled(false);
    const timeoutId = window.setTimeout(
      () => setWorkbenchPreparingStalled(true),
      WORKBENCH_PREPARING_MAX_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [workbenchPreparingActive, workbenchBootStatus, workbenchSnapshotBootstrap]);

  const workbenchPreparing = workbenchPreparingActive && !workbenchPreparingStalled;

  const isWorkbenchBuildLive = useMemo(() => {
    const streamsIdle = !workbenchStreamLoading && !workbenchIsStreaming;
    if (workbenchBuildStatus === 'success' && streamsIdle) {
      return false;
    }
    return Boolean(workbenchStreamLoading || workbenchIsStreaming);
  }, [workbenchStreamLoading, workbenchIsStreaming, workbenchBuildStatus]);

  /** Show Finalizing/Done only after streams idle and WebContainer production build succeeded */
  const workbenchTimelineTerminalVisible = useMemo(() => {
    if (!isStreamingPrototype || !streamingWorkbenchLlmInteractionId) return false;
    const idle = !facade.state.isStreaming && !workbenchStreamLoading && !workbenchIsStreaming;
    return idle && workbenchBuildStatus === 'success';
  }, [
    isStreamingPrototype,
    streamingWorkbenchLlmInteractionId,
    facade.state.isStreaming,
    workbenchStreamLoading,
    workbenchIsStreaming,
    workbenchBuildStatus,
  ]);

  /**
   * Boots Nest → Flask → Chef SSE.
   * Flask injects synthesized prototyper text; this bootstrap literal must match Flask's
   * `WORKBENCH_BUILD_STREAM_TRIGGER` for continuation merge positioning.
   */
  const handleStreamingPrototypeStart = useCallback((ctx?: { llmInteractionId?: string }) => {
    if (ctx?.llmInteractionId) {
      // A build-stream only starts here for a turn `shouldStartBuildStream` decided is
      // genuinely new (never `skip`/`resume-build`) — i.e. a fresh prototype generation,
      // not a restore of one already built. `currentPrototyper` in Redux is a single flat
      // record with no per-turn key, so without this reset it kept showing the *previous*
      // generation's slug/demo-link id: publishing prototype #1, then generating #2 in the
      // same conversation, left the Publish menu reporting #1's "already published" state
      // for #2 (which was never published) until #2 itself got its own demo-link id.
      if (streamingBuildLlmInteractionIdRef.current !== ctx.llmInteractionId) {
        facade.prototyper.cancelPrototype();
      }
      streamingBuildLlmInteractionIdRef.current = ctx.llmInteractionId;
      setStreamingWorkbenchLlmInteractionId(ctx.llmInteractionId);
    }
    pendingWorkbenchStreamStartRef.current = true;
    setWorkbenchStreamStartNonce((n) => n + 1);
  }, [facade.prototyper]);
  startAutoFixStreamRef.current = (llmInteractionId: string) =>
    handleStreamingPrototypeStart({ llmInteractionId });

  /**
   * Start build-stream only after React commits `streamingWorkbenchLlmInteractionId` so
   * `useChat` id is not still `:pending` when the first POST runs (avoids remount + `.parts` errors).
   */
  useEffect(() => {
    if (!pendingWorkbenchStreamStartRef.current) return;
    const expectedId = streamingBuildLlmInteractionIdRef.current;
    if (expectedId != null && streamingWorkbenchLlmInteractionId !== expectedId) {
      return;
    }
    pendingWorkbenchStreamStartRef.current = false;
    startGeneration(WORKBENCH_BUILD_STREAM_TRIGGER);
  }, [streamingWorkbenchLlmInteractionId, workbenchStreamStartNonce, startGeneration]);

  /** Reset streaming/workbench refs when the conversation changes (not on mount or stop identity churn). */
  useEffect(() => {
    const id = facade.state.currentConversationId ?? null;
    if (lastResetConversationIdRef.current === undefined) {
      lastResetConversationIdRef.current = id;
      return;
    }
    if (lastResetConversationIdRef.current === id) return;
    lastResetConversationIdRef.current = id;
    stopWorkbenchStreamRef.current();
    streamingBuildLlmInteractionIdRef.current = undefined;
    setStreamingWorkbenchLlmInteractionId(null);
    pendingWorkbenchStreamStartRef.current = false;
    setStreamingPrototypePanelVisible(false);
    // Hot Edit's selection is otherwise only cleared when the live preview URL changes —
    // which it doesn't on a conversation switch, since the WebContainer is shared/persists
    // across chats. Without this, a stale selection (a specific file + line from the OLD
    // chat) stays active and the next edit patches that file inside the NEW conversation.
    hotEditStore.mode.set(false);
    hotEditStore.selection.set(null);
  }, [facade.state.currentConversationId]);

  // The prototype of the version open in the workbench, for the Data view's "This prototype".
  const workbenchPrototypeId =
    workbenchActiveSnapshotInteractionId && workbenchTurnVersions?.status === 'ready'
      ? (workbenchTurnVersions.get(workbenchActiveSnapshotInteractionId)?.prototypeId ?? null)
      : null;

  const renderWorkbenchPanel = useMemo(
    () =>
      (props: {
        isOpen: boolean;
        onClose: () => void;
        onToggleConversationPanel: () => void;
        isConversationPanelCollapsed: boolean;
        embedded?: boolean;
        headerLeftSlot?: React.ReactNode;
        headerRightSlot?: React.ReactNode;
        headerRightLeadSlot?: React.ReactNode;
      }) => (
        <WorkbenchPanel
          isOpen={props.isOpen}
          onClose={props.onClose}
          onToggleConversationPanel={props.onToggleConversationPanel}
          isConversationPanelCollapsed={props.isConversationPanelCollapsed}
          embedded={props.embedded}
          headerLeftSlot={props.headerLeftSlot}
          headerRightSlot={props.headerRightSlot}
          headerRightLeadSlot={props.headerRightLeadSlot}
          projectName={facade.state.name || 'prototype'}
          startupSlug={startupSlug}
          buildTimeline={isStreamingPrototype ? workbenchBuildTimeline : null}
          projectId={facade.state.currentProjectId ?? null}
          prototypeId={workbenchPrototypeId}
        />
      ),
    [facade.state.name, startupSlug, isStreamingPrototype, workbenchBuildTimeline, facade.state.currentProjectId, workbenchPrototypeId]
  );

  // All WebContainer/Prototyper wiring, bundled once — same mode-gating as
  // before, just no longer drilled as ~25 individual props.
  const workbench: ConversationWorkbench = {
    isStreamingPrototype,
    onStreamingPrototypeStart: handleStreamingPrototypeStart,
    workbenchSnapshotBootstrap: isStreamingPrototype ? workbenchSnapshotBootstrap : undefined,
    workbenchSnapshotRestoreError: isStreamingPrototype ? workbenchSnapshotRestoreError : undefined,
    workbenchSnapshotRestoreNotice: isStreamingPrototype ? workbenchSnapshotRestoreNotice : null,
    workbenchSnapshotRestoreRetrying: isStreamingPrototype
      ? workbenchSnapshotRestoreRetrying
      : false,
    workbenchSnapshotIndexLoading: isStreamingPrototype ? workbenchSnapshotIndexLoading : undefined,
    getWorkbenchSnapshotIndexEntry: isStreamingPrototype
      ? workbenchGetSnapshotIndexEntry
      : undefined,
    onRetrySnapshotBootstrap: isStreamingPrototype ? workbenchRetrySnapshotBootstrap : undefined,
    activeSnapshotInteractionId: isStreamingPrototype ? workbenchActiveSnapshotInteractionId : null,
    onPrototypePublished: handlePrototypePublished,
    restoreInteractionLoadingId: isStreamingPrototype ? workbenchRestoreInteractionLoadingId : null,
    streamingWorkbenchLoading: isStreamingPrototype ? workbenchPreparing : false,
    onStreamingWorkbenchPrototypePanelVisibilityChange: isStreamingPrototype
      ? setStreamingPrototypePanelVisible
      : undefined,
    renderWorkbenchPanel,
    streamingPublishReady,
    streamingPublishDisabledReason: isStreamingPrototype ? streamingPublishDisabledReason : null,
    streamingPreviewReady: isStreamingPrototype ? workbenchPreviewReady : undefined,
    streamingWorkbenchLlmInteractionId: isStreamingPrototype
      ? streamingWorkbenchLlmInteractionId
      : null,
    livePrototyperBuildTimeline: isStreamingPrototype ? workbenchBuildTimeline : null,
    isWorkbenchBuildLive: isStreamingPrototype ? isWorkbenchBuildLive : false,
    workbenchTimelineTerminalVisible: isStreamingPrototype
      ? workbenchTimelineTerminalVisible
      : false,
    onPrototyperWorkbenchRestore:
      isStreamingPrototype && facade.state.currentProjectId && facade.state.currentConversationId
        ? restorePrototypeSnapshotForInteraction
        : undefined,
    uploadVerifiedPrototypeSnapshot: isStreamingPrototype
      ? uploadVerifiedPrototypeSnapshot
      : undefined,
    resumeInterruptedBuild:
      isStreamingPrototype && facade.state.currentProjectId && facade.state.currentConversationId
        ? resumeInterruptedBuild
        : undefined,
    // The Stop button aborts the generation; conversation switches only stop reading.
    onCancelWorkbenchStream: isStreamingPrototype ? stopAndAbortWorkbenchStream : undefined,
    workbenchPersistError: isStreamingPrototype ? workbenchPersistError : undefined,
    prototypeFixAttention: isStreamingPrototype ? workbenchVersionAttention : null,
    prototypeFixCanUndo: workbenchVersionAttentionCanUndo,
    prototypeFixBusy: workbenchVersionAttentionBusy,
    workbenchAutoFixLabel: isStreamingPrototype ? workbenchAutoFixLabel : null,
    onTryAnotherFix: isStreamingPrototype ? workbenchTryAnotherFix : undefined,
    onUndoPrototypeChange: isStreamingPrototype ? workbenchUndoFailedChange : undefined,
    loadPrototypeFixDetails: isStreamingPrototype
      ? workbenchLoadVersionAttentionDetails
      : undefined,
    turnVersions: isStreamingPrototype ? workbenchTurnVersions : undefined,
    resumeFailedVersion:
      isStreamingPrototype && facade.state.currentProjectId && facade.state.currentConversationId
        ? workbenchResumeFailedVersion
        : undefined,
    resumeDraftFromServerFiles:
      isStreamingPrototype && facade.state.currentProjectId && facade.state.currentConversationId
        ? workbenchResumeDraftFromServerFiles
        : undefined,
    repairPreviewCrash: isStreamingPrototype ? workbenchRepairPreviewCrash : undefined,
    openPrototypePanelFromLibrary: fromLibrary,
  };

  return (
    <ConversationProvider
      value={{
        facade,
        isWeb: true,
        urlConversationId: urlConversationId || null,
        openArtifactIdFromLibrary,
        openArtifactInteractionId,
        authDialogState: authDialog,
        // Scope pages force the header back to Work Scopes + re-resolve on 404.
        onBack: scopeModeControl?.onBack,
        onConversationNotFound: scopeModeControl?.onConversationNotFound,
        workbench,
      }}
    >
      <ConversationModule />
    </ConversationProvider>
  );
}
